import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AppSettingsContext = createContext(null);

// Default URL settings (used before settings are loaded)
const defaultUrls = {
  admin_url: '',
  redirects_url: '',
  pages_url: '',
};

export function AppSettingsProvider({ children }) {
  const [urls, setUrls] = useState(defaultUrls);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/settings/urls`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setUrls({
            admin_url: data.data.admin_url || '',
            redirects_url: data.data.redirects_url || '',
            pages_url: data.data.pages_url || '',
          });
        }
      }
    } catch (err) {
      console.error('Error fetching URL settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Helper function to get the short URL domain (without protocol)
  const getShortUrlDomain = useCallback(() => {
    if (!urls.redirects_url) return 'your-domain.com';
    try {
      const url = new URL(urls.redirects_url);
      return url.host;
    } catch {
      // If it's not a valid URL, return as-is (might be just domain)
      return urls.redirects_url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    }
  }, [urls.redirects_url]);

  // Helper function to get the pages domain (without protocol)
  const getPagesDomain = useCallback(() => {
    if (!urls.pages_url) return 'your-domain.com';
    try {
      const url = new URL(urls.pages_url);
      return url.host;
    } catch {
      return urls.pages_url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    }
  }, [urls.pages_url]);

  // Build a full short URL
  const buildShortUrl = useCallback((slug) => {
    const domain = getShortUrlDomain();
    return `${domain}/${slug}`;
  }, [getShortUrlDomain]);

  // Build a full landing page URL
  const buildLandingPageUrl = useCallback((slug) => {
    const domain = getPagesDomain();
    return `${domain}/p/${slug}`;
  }, [getPagesDomain]);

  // Build a full bio link URL
  const buildBioLinkUrl = useCallback((username) => {
    const domain = getPagesDomain();
    return `${domain}/b/${username}`;
  }, [getPagesDomain]);

  // Build a full QR code redirect URL
  const buildQrRedirectUrl = useCallback((code) => {
    const domain = getShortUrlDomain();
    return `${domain}/r/${code}`;
  }, [getShortUrlDomain]);

  const value = {
    urls,
    loading,
    error,
    refreshSettings: fetchSettings,
    // Helper functions
    getShortUrlDomain,
    getPagesDomain,
    buildShortUrl,
    buildLandingPageUrl,
    buildBioLinkUrl,
    buildQrRedirectUrl,
  };

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings() {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
}

export default AppSettingsContext;
